import { theme } from '@Guidelines/Theme/theme';
import type AccountMetricsView from '@UI/Store/AccountMetrics/Views/AccountMetricsView';
import { css } from '@emotion/react';
import type * as React from 'react';

const cardStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 1.5rem;
	border-radius: ${theme.radii.xlarge};
	background-color: ${theme.colors.surface};
	border: 1.5px solid ${theme.colors.border};
`;

const headerStyle = css`
	${theme.typography.metric}
	letter-spacing: 0.02em;
	color: ${theme.colors.ink};
`;

const rowsStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
`;

const rowStyle = css`
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 1rem;
	padding: 0.625rem 0.875rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
`;

const rowNameStyle = css`
	font-family: ${theme.fonts.body};
	font-weight: ${theme.fontWeights.medium};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.ink};
`;

const rowCountStyle = css`
	font-family: ${theme.fonts.mono};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

const pluralize = (count: number, singular: string, plural: string): string =>
	count > 1 ? plural : singular;

type Props = {
	readonly metrics: AccountMetricsView;
};

/**
 * The consolidated block (`ORGANIZATION_CONSOLIDATED_READ`): a header aggregating every organization
 * of the account, then one row per organization with its own facility/user counts.
 */
const ConsolidatedMetrics: React.FC<Props> = ({ metrics }) => {
	return (
		<div css={cardStyle}>
			<p css={headerStyle}>
				{metrics.organizationCount} {pluralize(metrics.organizationCount, 'structure', 'structures')}{' '}
				· {metrics.facilityCount} {pluralize(metrics.facilityCount, 'agence', 'agences')} ·{' '}
				{metrics.userCount} {pluralize(metrics.userCount, 'utilisateur', 'utilisateurs')}
			</p>
			<div css={rowsStyle}>
				{metrics.organizations.map((organization) => (
					<div key={organization.id} css={rowStyle}>
						<span css={rowNameStyle}>{organization.name}</span>
						<span css={rowCountStyle}>
							{organization.facilityCount}{' '}
							{pluralize(organization.facilityCount, 'agence', 'agences')} ·{' '}
							{organization.userCount}{' '}
							{pluralize(organization.userCount, 'utilisateur', 'utilisateurs')}
						</span>
					</div>
				))}
			</div>
		</div>
	);
};

export default ConsolidatedMetrics;
